import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import { OfferSummary } from "../OfferSummary";
import { Offer } from "../OfferCard/interfaces";
import { createRecallSchema, RecallFormValues } from "./schema";

export type { RecallFormValues } from "./schema";

interface RecallReason {
  id: number;
  text: string;
}

interface RecallOfferModalProps {
  offer?: Offer;
  isOpen: boolean;
  onCancel: () => void;
  onValidateRecall: (offer: Offer, values: RecallFormValues) => Promise<unknown>;
  reasons: RecallReason[];
  // What else recalling this offer settles, e.g. a demand's delivery cost on its last open line.
  warning?: string;
}

const DEFAULT_VALUES: RecallFormValues = { recall_reason: "", recall_comments: "" };

export const RecallOfferModal = ({
  offer,
  isOpen,
  onCancel,
  onValidateRecall,
  reasons,
  warning,
}: RecallOfferModalProps) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<RecallFormValues>({
    resolver: zodResolver(createRecallSchema(t("traderDetails.form.mandatoryFieldBlankError"))),
    defaultValues: DEFAULT_VALUES,
  });

  // Each recall starts from a clean form, as redux-form's enableReinitialize did.
  useEffect(() => {
    if (isOpen) {
      reset(DEFAULT_VALUES);
    }
  }, [isOpen, reset]);

  // Closing is owned by the parent (via onValidateRecall) so a successful recall
  // can close everything while Cancel can return to the comments thread.
  const onSubmit = handleSubmit(values => (offer ? onValidateRecall(offer, values) : undefined));

  const selectedReason = watch("recall_reason");

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onCancel()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("recallModal.heading")}</DialogTitle>
          <DialogDescription>{t("recallModal.instructionText")}</DialogDescription>
        </DialogHeader>

        {offer && <OfferSummary offer={offer} />}

        {warning && (
          <Alert variant="warning">
            <AlertDescription>{warning}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="recall_reason" className="font-semibold text-foreground">
              {`${t("recallOfferModal.reason")} *`}
            </Label>
            <Select
              value={selectedReason}
              onValueChange={value =>
                setValue("recall_reason", value, { shouldValidate: true, shouldDirty: true })
              }
            >
              <SelectTrigger
                id="recall_reason"
                className="w-full"
                aria-invalid={!!errors.recall_reason}
              >
                <SelectValue placeholder={t("recallOfferModal.reasonPlaceholder")} />
              </SelectTrigger>
              <SelectContent position="popper" className="max-h-72">
                {(reasons || []).map(reason => (
                  <SelectItem key={reason.id} value={String(reason.id)}>
                    {reason.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.recall_reason && (
              <p id="recall_reason-error" role="alert" className="text-destructive text-sm">
                {errors.recall_reason.message}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="recall_comments" className="font-semibold text-foreground">
              {`${t("recallOfferModal.comments")} *`}
            </Label>
            <Textarea
              id="recall_comments"
              placeholder={t("recallOfferModal.commentPlaceholder")}
              aria-invalid={!!errors.recall_comments}
              {...register("recall_comments")}
            />
            {errors.recall_comments && (
              <p id="recall_comments-error" role="alert" className="text-destructive text-sm">
                {errors.recall_comments.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <WFPButton type="button" variant="secondary" onClick={onCancel}>
              {t("common.cancel")}
            </WFPButton>
            <WFPButton type="submit" variant="destructive" disabled={isSubmitting}>
              {t("recallModal.submitButtonText")}
            </WFPButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

interface RecallReasonsState {
  listRecallReasons: { data: { results?: RecallReason[] } };
}

export const mapStateToProps = (state: RecallReasonsState) => ({
  reasons: state.listRecallReasons?.data.results ?? [],
});

export const ConnectedRecallOfferModal = connect(mapStateToProps)(RecallOfferModal);
